import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

import { useToast } from "@/hooks/use-toast";
import { CreditCard, Loader2, Check } from "lucide-react";
import { getCardDesign, TOTAL_CARD_VARIANTS, type CardVariant } from "@/lib/card-designs";
import { useOrgSettings } from "@/lib/org-settings-context";

interface RequestCardDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

const variants = Array.from({ length: TOTAL_CARD_VARIANTS }, (_, i) => (i + 1) as CardVariant);

export function RequestCardDialog({ open, onOpenChange }: RequestCardDialogProps) {
    const { toast } = useToast();
    const { currencySymbol } = useOrgSettings();
    const [nickname, setNickname] = useState("");
    const [monthlyLimit, setMonthlyLimit] = useState("50000");
    const [dailyLimit, setDailyLimit] = useState("10000");
    const [selectedDesign, setSelectedDesign] = useState<CardVariant>(1);

    const requestCardMutation = useMutation({
        mutationFn: async (data: {
            nickname: string;
            cardType: string;
            monthlyLimitCents: number;
            dailyLimitCents: number;
            cardDesign: number;
        }) => {
            const response = await apiRequest("POST", "/api/customer/cards", data);
            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.message || "Failed to request card");
            }
            return response.json();
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["customer-cards"] });
            toast({
                title: "Request Submitted",
                description: "Your card request is pending approval. You'll be notified once it's approved.",
            });
            onOpenChange(false);
            setNickname("");
            setMonthlyLimit("50000");
            setDailyLimit("10000");
            setSelectedDesign(1);
        },
        onError: (error: Error) => {
            toast({
                title: "Error",
                description: error.message,
                variant: "destructive",
            });
        },
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        requestCardMutation.mutate({
            nickname: nickname || "My Card",
            cardType: "VIRTUAL",
            monthlyLimitCents: parseInt(monthlyLimit) * 100,
            dailyLimitCents: parseInt(dailyLimit) * 100,
            cardDesign: selectedDesign,
        });
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <CreditCard className="h-5 w-5" />
                        Request New Card
                    </DialogTitle>
                    <DialogDescription>
                        Choose your card design and fill in the details below.
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Card Design Picker - 3x3 grid */}
                    <div className="space-y-2">
                        <Label>Card Design</Label>
                        <div className="grid grid-cols-3 gap-2">
                            {variants.map((variant) => {
                                const design = getCardDesign(variant);
                                const isSelected = selectedDesign === variant;
                                return (
                                    <button
                                        key={variant}
                                        type="button"
                                        onClick={() => setSelectedDesign(variant)}
                                        className={cn(
                                            "relative rounded-lg overflow-hidden transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                                            "aspect-[1.6/1]",
                                            isSelected
                                                ? "ring-2 ring-primary ring-offset-2 shadow-md"
                                                : "ring-1 ring-border/50 opacity-60 hover:opacity-100 hover:ring-border"
                                        )}
                                    >
                                        <img
                                            src={design.front}
                                            alt={`Design ${variant}`}
                                            className="w-full h-full object-cover"
                                        />
                                        {isSelected && (
                                            <div className="absolute top-1 right-1 h-4 w-4 rounded-full bg-primary flex items-center justify-center shadow-sm">
                                                <Check className="h-2.5 w-2.5 text-primary-foreground" />
                                            </div>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Card Nickname */}
                    <div className="space-y-2">
                        <Label htmlFor="nickname">Card Nickname (Optional)</Label>
                        <Input
                            id="nickname"
                            placeholder="e.g., Shopping Card, Travel Card"
                            value={nickname}
                            onChange={(e) => setNickname(e.target.value)}
                            maxLength={50}
                        />
                    </div>

                    {/* Monthly Limit */}
                    <div className="space-y-2">
                        <Label htmlFor="monthlyLimit">Monthly Spending Limit ({currencySymbol})</Label>
                        <Input
                            id="monthlyLimit"
                            type="number"
                            min="1000"
                            max="500000"
                            step="1000"
                            value={monthlyLimit}
                            onChange={(e) => setMonthlyLimit(e.target.value)}
                            required
                        />
                        <p className="text-xs text-muted-foreground">
                            Minimum: {currencySymbol}1,000 • Maximum: {currencySymbol}5,00,000
                        </p>
                    </div>

                    {/* Daily Limit */}
                    <div className="space-y-2">
                        <Label htmlFor="dailyLimit">Daily Spending Limit ({currencySymbol})</Label>
                        <Input
                            id="dailyLimit"
                            type="number"
                            min="500"
                            max="100000"
                            step="500"
                            value={dailyLimit}
                            onChange={(e) => setDailyLimit(e.target.value)}
                            required
                        />
                        <p className="text-xs text-muted-foreground">
                            Minimum: {currencySymbol}500 • Maximum: {currencySymbol}1,00,000
                        </p>
                    </div>

                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            disabled={requestCardMutation.isPending}
                        >
                            Cancel
                        </Button>
                        <Button type="submit" disabled={requestCardMutation.isPending}>
                            {requestCardMutation.isPending ? (
                                <>
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    Requesting...
                                </>
                            ) : (
                                <>
                                    <CreditCard className="h-4 w-4 mr-2" />
                                    Request Card
                                </>
                            )}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
